import React, { useEffect, useState } from "react";
import { Card, Row, Col, Space, Typography, Tag } from "antd";
import { Link } from "react-router-dom";
import { groupedTools } from "../data/toolsData";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from '../components/StandardButton';
import { localizedPath } from '../utils/paths';

const { Title, Paragraph } = Typography;

const tools = Object.entries(groupedTools).map(([category, items]) => ({
  category,
  items,
}));

const TOOL_USAGE_KEY = "tool_usage";

const getToolUsage = () => {
  const stored = localStorage.getItem(TOOL_USAGE_KEY);
  return stored ? JSON.parse(stored) : {};
};

const incrementToolUsage = (toolName) => {
  const usage = getToolUsage();
  usage[toolName] = (usage[toolName] || 0) + 1;
  localStorage.setItem(TOOL_USAGE_KEY, JSON.stringify(usage));
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeTools, setActiveTools] = useState([]);

  const categories = [{ key: 'All', label: t('All') || 'All' }, ...tools?.map((toolsItem) => {
    return {
      key: toolsItem.category, 
      label: t(toolsItem.category) || toolsItem.category
    }
  })];

  const getToolsList = () => {
    const filteredTools = tools
      .map((category) => ({
        ...category,
        items: category.items.filter((tool) => {
          tool.category = category.category
          const translatedName = t(tool.nameKey);
          const nameToSearch = translatedName === tool.nameKey ? tool.name : translatedName;
          return nameToSearch.toLowerCase();
        }),
      }))
      .filter((category) => category.items.length > 0);

    return filteredTools;
  }

  useEffect(() => {
    if (getToolsList()?.length) {
      setActiveTools(activeCategory === "All" ? getToolsList() : getToolsList().filter((t) => t.category === activeCategory))
    }
  }, [activeCategory])

  return (
    <>
      <Header handleActiveCatgory={(category) => setActiveCategory(category) }></Header>
      <div className="min-h-screen bg-gradient-to-b">
        <div className="max-w-7xl mx-auto relative">

          <div className="hero-section">
            <h1>{t("home_site_title")}</h1>
            <p>
              {t("quto_description")}
            </p>
          </div>

          <div className="mt-8">
            {/* Render Tool Categories */}
            <div style={{ textAlign: "center", display: "flex", justifyContent: "space-between" }} className="mb-8">
              {
                categories.map((category, index) => (
                  <StandardButton 
                    type="secondary" 
                    shape="round" 
                    size={'large'} 
                    key={`category_btn_${index}`} 
                    onClick={() => setActiveCategory(category.key)}
                  >
                    <span style={{ fontWeight: 'bold' }}>{category.label}</span>
                  </StandardButton>
                ))
              }

            </div>
            {/* Render Tool Categories Items */}
            {(() => {
              const multiItemCategories = activeTools.filter((cat) => cat.items.length > 0);
              return (
                <>
                  <Row gutter={[24, 24]} key={`category-row-container`}>
                    {multiItemCategories.map((category, categoryIndex) => (
                      <React.Fragment key={`category-${categoryIndex}`}>
                        {category.items.map((tool, toolIndex) => (
                          <Col xs={24} sm={12} md={8} lg={6} key={`${categoryIndex}-${toolIndex}`}>
                            <Link
                              to={localizedPath(i18n.language, tool.path)}
                              onClick={() => incrementToolUsage(tool.name)}
                            >

                              <Card
                                hoverable
                                style={{
                                  borderRadius: 12,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                  height: 300,
                                  body: {
                                    padding: '24px 16px',
                                    backgroundColor: '#f0f2f5'
                                  }
                                }}
                              >
                                <Space direction="vertical">
                                  <Space>
                                    <img src={tool.icon} alt={t(tool.nameKey)} className="w-20 h-20 rounded" loading="lazy" />
                                    {tool.isPaid && (
                                      <Tag
                                        color="#fff"
                                        style={{
                                          position: "absolute",
                                          top: 10,
                                          right: 10,
                                          background: "#ff4d4f",
                                          borderRadius: "20px",
                                          fontSize: 12,
                                          fontWeight: 600,
                                          padding: "2px 10px",
                                          lineHeight: "normal",
                                        }}
                                      >
                                        {t("paid_tool")}
                                      </Tag>
                                    )}
                                  </Space>
                                  <Title level={4} style={{ height: 45 }}>
                                    {t(tool.nameKey)}
                                  </Title>
                                  <Paragraph style={{ fontSize: 14 }} ellipsis={{ rows: 4, expandable: false, symbol: '...', tooltip: t(tool.descriptionKey) }}>
                                    {t(tool.descriptionKey)}
                                  </Paragraph>
                                </Space>
                              </Card>
                            </Link>
                          </Col>
                        ))}
                      </React.Fragment>
                    ))}
                  </Row>
                </>
              );
            })()}

            {activeTools.length === 0 && (
              <div className="text-center text-gray-500">{t("home_no_tools_found")}</div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default HomePage;
